import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Users, Briefcase, FileText, DollarSign, Scale, TrendingUp, Star } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReportsPage() {
  const { lang } = useLanguage();
  const [data, setData] = useState(null);
  const [enhanced, setEnhanced] = useState(null);

  const fetchReports = useCallback(async () => {
    try {
      const [res, enhRes] = await Promise.all([
        axios.get(`${API}/reports/overview`, { withCredentials: true }),
        axios.get(`${API}/reports/enhanced`, { withCredentials: true }),
      ]);
      setData(res.data);
      setEnhanced(enhRes.data);
    } catch {}
  }, []);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  if (!data) return (
    <DashboardLayout>
      <div className="flex items-center justify-center py-24">
        <div className="w-8 h-8 border-2 border-[#B48E4B] border-t-transparent rounded-full animate-spin" />
      </div>
    </DashboardLayout>
  );

  const statusLabels = {
    new: lang === 'ar' ? 'جديدة' : 'New',
    under_review: lang === 'ar' ? 'قيد المراجعة' : 'Under Review',
    in_progress: lang === 'ar' ? 'قيد التنفيذ' : 'In Progress',
    pending_documents: lang === 'ar' ? 'بانتظار المستندات' : 'Pending Documents',
    resolved: lang === 'ar' ? 'تم الحل' : 'Resolved',
    closed: lang === 'ar' ? 'مغلقة' : 'Closed',
  };

  const statusBarColors = {
    new: 'bg-sky-500', under_review: 'bg-amber-500', in_progress: 'bg-purple-500',
    pending_documents: 'bg-red-500', resolved: 'bg-green-500', closed: 'bg-slate-400'
  };

  const maxCaseCount = Math.max(...Object.values(data.cases_by_status || {}), 1);

  return (
    <DashboardLayout>
      <div data-testid="reports-page">
        <h1 className="text-xl sm:text-2xl font-bold text-[#0F172A] font-heading mb-6">
          {lang === 'ar' ? 'التقارير والإحصائيات' : 'Reports & Analytics'}
        </h1>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <KPI icon={Briefcase} label={lang === 'ar' ? 'إجمالي القضايا' : 'Total Cases'} value={data.total_cases} color="purple" />
          <KPI icon={Users} label={lang === 'ar' ? 'العملاء' : 'Clients'} value={data.clients_count} color="blue" />
          <KPI icon={Scale} label={lang === 'ar' ? 'المحامين' : 'Lawyers'} value={data.lawyers_count} color="amber" />
          <KPI icon={DollarSign} label={lang === 'ar' ? 'الإيرادات' : 'Revenue'} value={`${data.revenue} JOD`} color="green" />
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mb-6">
          {/* Cases by Status */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader><CardTitle className="text-base font-heading">{lang === 'ar' ? 'القضايا حسب الحالة' : 'Cases by Status'}</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {Object.entries(data.cases_by_status || {}).map(([status, count]) => (
                <div key={status}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-[#475569]">{statusLabels[status] || status}</span>
                    <span className="font-medium text-[#0F172A]">{count}</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2">
                    <div className={`h-2 rounded-full ${statusBarColors[status] || 'bg-slate-400'}`} style={{ width: `${(count / maxCaseCount) * 100}%` }} />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Cases by Type */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader><CardTitle className="text-base font-heading">{lang === 'ar' ? 'القضايا حسب النوع' : 'Cases by Type'}</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {Object.entries(data.cases_by_type || {}).map(([type, count]) => (
                <div key={type} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <span className="text-sm text-[#475569] capitalize">{type.replace('_', ' ')}</span>
                  <span className="text-lg font-bold text-[#0F172A]">{count}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Invoice Stats */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader><CardTitle className="text-base font-heading">{lang === 'ar' ? 'إحصائيات الفواتير' : 'Invoice Stats'}</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-3 bg-slate-50 rounded-lg">
                  <div className="text-2xl font-bold text-[#0F172A]">{data.total_invoices}</div>
                  <div className="text-xs text-[#94A3B8]">{lang === 'ar' ? 'الإجمالي' : 'Total'}</div>
                </div>
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">{data.paid_invoices}</div>
                  <div className="text-xs text-[#94A3B8]">{lang === 'ar' ? 'مدفوعة' : 'Paid'}</div>
                </div>
                <div className="text-center p-3 bg-amber-50 rounded-lg">
                  <div className="text-2xl font-bold text-amber-600">{data.pending_invoices}</div>
                  <div className="text-xs text-[#94A3B8]">{lang === 'ar' ? 'معلقة' : 'Pending'}</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Cases per Lawyer */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader><CardTitle className="text-base font-heading">{lang === 'ar' ? 'القضايا لكل محامي' : 'Cases per Lawyer'}</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {(data.cases_per_lawyer || []).map(l => (
                <div key={l.lawyer_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <span className="text-sm font-medium text-[#0F172A]">{l.name}</span>
                  <span className="text-lg font-bold text-[#B48E4B]">{l.cases}</span>
                </div>
              ))}
              {(!data.cases_per_lawyer || data.cases_per_lawyer.length === 0) && (
                <p className="text-sm text-[#94A3B8] text-center py-4">{lang === 'ar' ? 'لا توجد بيانات' : 'No data'}</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Enhanced Reports Section */}
        {enhanced && (
          <div className="grid lg:grid-cols-3 gap-6 mt-6">
            <Card className="border-slate-200 shadow-sm">
              <CardHeader><CardTitle className="text-base font-heading">{lang === 'ar' ? 'نسبة النجاح' : 'Success Ratio'}</CardTitle></CardHeader>
              <CardContent>
                <div className="text-center">
                  <div className="relative w-28 h-28 mx-auto mb-4">
                    <svg className="w-28 h-28 transform -rotate-90" viewBox="0 0 100 100">
                      <circle cx="50" cy="50" r="42" fill="none" stroke="#E2E8F0" strokeWidth="8" />
                      <circle cx="50" cy="50" r="42" fill="none" stroke="#059669" strokeWidth="8" strokeDasharray={`${enhanced.success_ratio * 2.64} 264`} strokeLinecap="round" />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-2xl font-bold text-[#0F172A]">{enhanced.success_ratio}%</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="bg-green-50 p-2 rounded"><span className="font-medium text-green-700">{enhanced.resolved_cases}</span><br/><span className="text-xs text-[#94A3B8]">{lang === 'ar' ? 'تم حلها' : 'Resolved'}</span></div>
                    <div className="bg-slate-50 p-2 rounded"><span className="font-medium text-[#475569]">{enhanced.closed_cases}</span><br/><span className="text-xs text-[#94A3B8]">{lang === 'ar' ? 'مغلقة' : 'Closed'}</span></div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200 shadow-sm">
              <CardHeader><CardTitle className="text-base font-heading">{lang === 'ar' ? 'تقييم العملاء' : 'Client Ratings'}</CardTitle></CardHeader>
              <CardContent>
                <div className="text-center mb-4">
                  <div className="text-4xl font-bold text-[#B48E4B]">{enhanced.avg_rating || '-'}</div>
                  <p className="text-sm text-[#94A3B8] mt-1">{enhanced.total_ratings} {lang === 'ar' ? 'تقييم' : 'reviews'}</p>
                </div>
                {(enhanced.lawyer_ratings || []).map(l => (
                  <div key={l.lawyer_id} className="flex items-center justify-between p-2 bg-slate-50 rounded mb-1">
                    <span className="text-sm">{l.name}</span>
                    <span className="text-sm font-medium text-[#B48E4B]">{l.avg_rating} ({l.total_reviews})</span>
                  </div>
                ))}
              </CardContent>
            </Card>
            <Card className="border-slate-200 shadow-sm">
              <CardHeader><CardTitle className="text-base font-heading">{lang === 'ar' ? 'ملخص' : 'Summary'}</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="p-3 bg-green-50 rounded-lg flex justify-between"><span className="text-sm">{lang === 'ar' ? 'مكتملة' : 'Completed'}</span><span className="font-bold text-green-700">{enhanced.total_completed}</span></div>
                <div className="p-3 bg-amber-50 rounded-lg flex justify-between"><span className="text-sm">{lang === 'ar' ? 'نشطة' : 'Active'}</span><span className="font-bold text-amber-700">{enhanced.total_cases - enhanced.total_completed}</span></div>
                <div className="p-3 bg-[#B48E4B]/10 rounded-lg flex justify-between"><span className="text-sm">{lang === 'ar' ? 'النشاطات' : 'Activities'}</span><span className="font-bold text-[#B48E4B]">{enhanced.recent_activity}</span></div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

function KPI({ icon: Icon, label, value, color }) {
  const colors = { blue: 'bg-[#E0F2FE] text-[#0284C7]', purple: 'bg-[#F3E8FF] text-[#7E22CE]', green: 'bg-[#D1FAE5] text-[#059669]', amber: 'bg-[#FEF3C7] text-[#D97706]' };
  return (
    <Card className="border-slate-200 shadow-sm">
      <CardContent className="p-4 sm:p-6">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${colors[color]}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="text-xl sm:text-2xl font-bold text-[#0F172A] font-heading">{value ?? '-'}</div>
        <div className="text-xs sm:text-sm text-[#475569] mt-1">{label}</div>
      </CardContent>
    </Card>
  );
}
